import moment from '../../../modules/moment';
import { getViewData } from '../../../modules/view-data';

export default {
    /**
     * Return the duration of the current search
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {String}
     */
    duration(state, getters) {
        return getters.endMoment
            .diff(getters.startMoment, 'hours', true)
            .toFixed(1)
            .replace('.0', '');
    },

    /**
     * Momentise the start date/time
     *
     * @param {Object} state
     *
     * @returns {*}
     */
    startMoment(state) {
        return new moment(`${ state.reservation_details.bookings[0].start_date } ${ state.reservation_details.bookings[0].start_time }`, 'DD-MM-YYYY HH:mm');
    },

    /**
     * Momentise the end date/time
     *
     * @param {Object} state
     *
     * @returns {*}
     */
    endMoment(state) {
        return new moment(`${ state.reservation_details.bookings[0].end_date } ${ state.reservation_details.bookings[0].end_time }`, 'DD-MM-YYYY HH:mm');
    },

    /**
     * Determine if the current booking is a monthly instant book
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {boolean}
     */
    isInstantMonthly(state, getters) {
        return state.reservation_details.rental === 'long'
            && getters.startMoment.format('DD-MM-YYYY') === new moment().format('DD-MM-YYYY');
    },

    /**
     * Determine if the current booking requires a deposit
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Boolean}
     */
    monthlyDepositRequired(state, getters) {
        return getters.isLongTerm
            && state.reservation_details.bookings[0].quote.deposit !== 0;
    },

    /**
     * Determine whether the current booking is monthly
     *
     * @param {Object} state
     *
     * @returns {Boolean}
     */
    isMonthly(state) {
        return Boolean(state.reservation_details.rental === 'long');
    },

    /**
     * Return the selected season interval data object
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Object}
     */
    seasonIntervalData(state, getters) {
        if (!getters.isSeason) {
            return null;
        }
        return state.space.lt_season_pricing.data
            .find(({ billing }) => billing.interval_count === state.reservation_details.interval_count);
    },

    /**
     * Return the max upsell season object
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @return {null|Object}
     */
    seasonUpsellObject(state, getters) {
        if (!getters.isSeason) {
            return null;
        }

        return getters.sortedSeasons[0];
    },

    /**
     * Return the lowest interval seasonal pricing
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Object}
     */
    lowestIntervalSeason(state, getters) {
        return getters.sortedSeasons[getters.sortedSeasons.length - 1];
    },

    /**
     * Return the season pricing data sorted by interval
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Array}
     */
    sortedSeasons(state, getters) {
        if (!getters.isSeason) {
            return [];
        }

        return state.space.lt_season_pricing.data.concat().sort((a, b) => {
            return (a.billing.interval_count < b.billing.interval_count)
                ? 1
                : -1;
        });
    },

    /**
     * Return the next payment date for the selected interval
     *
     * @param {Object} state
     *
     * @returns {undefined|moment.Moment}
     */
    renewalPaymentDate(state) {
        return state.reservation_details.bookings[0].quote.next_billing_date;
    },

    /**
     * Return the next payment date for a standard monthly booking
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {moment.Moment}
     */
    nextPaymentDate(state, getters) {
        if (getters.monthlyDepositRequired) {
            return getters.startMoment;
        }

        if (getters.seasonIntervalData) {
            const seasonLength = getters.seasonIntervalData.billing.interval_count;

            return moment(getters.startMoment)
                .add(seasonLength, 'months')
                .subtract(2, 'days');
        }

        return moment(getters.startMoment)
            .add(1, 'months')
            .subtract(2, 'days');
    },

    /**
     * Check whether the current space is and can be long term
     *
     * @param {Object} state
     *
     * @returns {Boolean|null}
     */
    isLongTerm(state) {
        return state.reservation_details.rental === 'long'
            && state.space.rental_types && state.space.rental_types.long;
    },

    /**
     * Check whether the current space is and can be short term
     *
     * @param {Object} state
     *
     * @returns {Boolean|null}
     */
    isShortTerm(state) {
        return state.reservation_details.rental === 'short'
            && state.space.rental_types && state.space.rental_types.short;
    },

    /**
     * Return whether the reservation is for a season
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Boolean}
     */
    isSeason(state, getters) {
        return getters.isLongTerm
            && state.reservation_details.rental === 'long'
            && state.space.lt_pricing_type === 'season'
            && state.space.approved;
    },

    /**
     * Return whether the season ticket is a rolling monthly reservation
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Boolean}
     */
    isSeasonRolling(state, getters) {
        return getters.isSeason
            && getters.seasonIntervalData
            && getters.seasonIntervalData.billing.interval_count === 1;
    },

    /**
     * Return if the bookings array only contains one booking
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Boolean}
     */
    isSingleBooking(state, getters) {
        return getters.bookings.length <= 1;
    },

    /**
     * Return the bookings array
     *
     * @param {Object} state
     *
     * @returns {Array}
     */
    bookings(state) {
        return state.reservation_details.bookings;
    },

    /**
     * Return the applied voucher discounted value
     *
     * @param {Object} state
     *
     * @returns {Number|null}
     */
    discountValue(state) {
        return state.reservation_details.voucher.discounted_value;
    },

    /**
     * Return the applied voucher code.
     *
     * @param {Object} state
     *
     * @returns {string}
     */
    discountCode(state) {
        return state.reservation_details.voucher.code;
    },

    /**
     * Return the sum of all deposit values returned from the Quoter API
     *
     * @param {Object} state
     *
     * @returns {Number}
     */
    depositValue(state) {
        return state.reservation_details.bookings
            .reduce((accumulator, booking) => accumulator + booking.quote.deposit, 0);
    },

    /**
     * Return the sum of all values returned from the Quoter API
     *
     * @param {Object} state
     *
     * @returns {Number}
     */
    quoteValue(state) {
        return state.reservation_details.bookings
            .reduce((accumulator, booking) => accumulator + booking.quote.value, 0);
    },

    /**
     * Return the total quote and deposit amount
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Number|String}
     */
    quoteAndDepositValue(state, getters) {
        let value = getters.depositValue + getters.arrangementFee;
        const serviceFee = state.serviceFee;

        if (getters.isInstantMonthly) {
            value += getters.quoteValue;
        }

        if (!getters.monthlyDepositRequired) {
            return getters.quoteValue + serviceFee + getters.arrangementFee;
        }

        return value;
    },

    /**
     * Get the arrangement fee.
     *
     * @param {object} state
     *
     * @returns {number}
     */
    arrangementFee(state) {
        return state.reservation_details.bookings[0].quote.arrangementFee;
    },

    /**
     * Return the value of the booking with discounts applied
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Number|String}
     */
    bookingValue(state, getters) {
        const additionalPayment = state.reservation_details.bookings[0].quote.additionalPayment;

        if (additionalPayment || additionalPayment === 0) {
            return additionalPayment; // '0' is still a value for additional payments
        }

        let quote = getters.depositValue + getters.arrangementFee;

        if (getters.discountValue !== null) {
            quote = getters.discountValue + getters.arrangementFee;

            if ((!getters.monthlyDepositRequired && getters.isLongTerm) || getters.isShortTerm) {
                return quote;
            }
        }

        if (getters.isInstantMonthly || getters.isShortTerm) {
            return quote += getters.quoteValue;
        }

        if (getters.monthlyDepositRequired) {
            return quote;
        }

        return getters.quoteValue + getters.arrangementFee;
    },

    /**
     * Return the value of the booking with discounts and service fee applied
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Number|String}
     */
    bookingValueWithServiceFee(state, getters) {
        const serviceFee = state.serviceFee;
        const bookingValue = getters.bookingValue;

        if (bookingValue === 0) {
            return  bookingValue;
        }

        return  bookingValue + serviceFee;
    },
    /**
     * Return the value of the booking without discounts applied
     *
     * @param {Object} state
     * @param {Object} getters
     *
     * @returns {Number|String}
     */
    bookingValueBeforeDiscount(state, getters) {
        if (getters.isShortTerm) {
            return getters.quoteValue;
        }

        let quote = getters.depositValue;

        if (getters.isInstantMonthly) {
            return quote += getters.quoteValue;
        }

        if (quote || getters.monthlyDepositRequired) {
            return quote;
        }

        return getters.quoteValue;
    },

    overDay(state) {
        return state.overDay;
    },
    /**
     * Return the value that defines if site is a whitelabel site
     *
     * @returns {Boolean}
     */
    whitelabel() {
        return getViewData('whitelabel');
    },

    /**
     * Return the value of the service fee.
     *
     * @returns {number}
     */
    serviceFee(state) {
        return state.serviceFee;
    },

    /**
     * Return the value of the service fee status.
     *
     * @returns {boolean}
     */
    serviceFeesStatus(state) {
        return state.serviceFeesStatus;
    },

    /**
     * Return id of reservation.
     *
     * @returns {number}
     */
    reservationId(state) {
        return state.reservation_details.bookings[0].reservation.reservation_id;
    },

    /**
     * Return interval count.
     *
     * @returns {number}
     */
    intervalCount(state) {
        return state.reservation_details.interval_count;
    }
};
